import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  User, 
  Users as UsersIcon,
  Target,
  Activity,
  Code,
  Calendar,
  Filter,
  RefreshCw,
  CheckCircle,
  Star,
  Info
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell
} from 'recharts';
import DatePicker from 'react-datepicker';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import api, { endpoints } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  MAX_ANALYTICS_RANGE_DAYS,
  applyDateRangeChange,
  getMaxEndDate,
  getQuickDateRange
} from '../utils/dateRange';
import {
  formatIntegerMetric,
  metricNumber,
  sumUserRequestTotals
} from '../utils/dailyMetrics';
import "react-datepicker/dist/react-datepicker.css";

const Users = () => {
  const [loading, setLoading] = useState(true);
  const [usersData, setUsersData] = useState(null);
  const [teamsData, setTeamsData] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: startOfDay(subDays(new Date(), 14)),
    endDate: endOfDay(new Date())
  });
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [includeIT, setIncludeIT] = useState(true);
  const [sortBy, setSortBy] = useState('ewps');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedRange, setSelectedRange] = useState(null);
  const [lowEwpsUsers, setLowEwpsUsers] = useState([]);

  const loadUsersData = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {
        startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
        endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
        limit: 100, // Limit to 100 developers for better performance
        includeIT: includeIT
      };

      if (selectedTeam !== 'all') {
        params.teamId = selectedTeam;
      }
      
      const teamParams = {
        includeIT: includeIT
      };

      const [usersRes, teamsRes] = await Promise.all([
        api.get(`${endpoints.analytics.developers}?${new URLSearchParams(params)}`),
        api.get(`${endpoints.analytics.teams}?${new URLSearchParams(teamParams)}`)
      ]);

      console.log('Users API response:', usersRes.data);
      console.log('Teams API response:', teamsRes.data);
      
      setUsersData(usersRes.data);
      setTeamsData(teamsRes.data.teams || []);
      setSelectedRange(null); // Reset selected range when data changes
    } catch (error) {
      console.error('Failed to load users data:', error);
              toast.error('Failed to load developer analytics');
      
      // Set empty data structure
      setUsersData({
        dateRange: { 
          startDate: format(dateRange.startDate, 'yyyy-MM-dd'), 
          endDate: format(dateRange.endDate, 'yyyy-MM-dd') 
        },
        users: [],
        dailyActivity: []
      });
      setTeamsData([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedTeam, includeIT]);

  useEffect(() => {
    loadUsersData();
  }, [loadUsersData]);

  const handleDateRangeChange = (field, date) => {
    const result = applyDateRangeChange(dateRange, field, date);
    if (!result.ok) {
      if (result.error) toast.error(result.error);
      return;
    }
    setDateRange(result.range);
  };

  const calculateExecutiveSummary = () => {
    if (!usersData?.users?.length) {
      return {
        totalUsers: 0,
        activeUsers: 0,
        avgProductivity: 0,
        topPerformer: null,
        avgAcceptanceRate: 0,
        avgActiveDays: 0
      };
    }

    const users = usersData.users;
    const totalUsers = users.length;
    const activeUsers = users.filter(user => (user.activeDays || 0) > 0).length;
    // Calculate total requests across all users
    const totalRequests = users.reduce((sum, user) => sum + sumUserRequestTotals(user), 0);
    
    // Calculate average requests per active user
    const avgProductivity = activeUsers > 0 ? Math.round(totalRequests / activeUsers) : 0;
    
    // Calculate Engagement Weighted Performance Score (EWPS) for each user
    // EWPS = totalRequests + (0.08 × totalTabsAccepted) + [ ln(1 + totalLinesAdded) / ln(1 + 1000) × 0.5 × totalRequests ]
    const topPerformer = users.reduce((top, user) => {
      // Calculate total requests (including bugbot)
      const totalRequests = sumUserRequestTotals(user);
      
      // Calculate EWPS using the new formula
      const tabsAccepted = metricNumber(user.totalTabsAccepted);
      const lines = metricNumber(user.totalLinesAdded);
      const normalizedLines = Math.log(1 + lines) / Math.log(1 + 1000);
      const userEWPS = (totalRequests || 0) 
        + 0.08 * tabsAccepted 
        + 0.5 * (totalRequests || 0) * (isFinite(normalizedLines) && normalizedLines > 0 ? normalizedLines : 0);
      
      // Calculate EWPS for the current top performer
      const topEWPS = top ? (() => {
        const topReq = sumUserRequestTotals(top);
        const topTabs = metricNumber(top.totalTabsAccepted);
        const topLines = metricNumber(top.totalLinesAdded);
        const topNorm = Math.log(1 + topLines) / Math.log(1 + 1000);
        return topReq + 0.08 * topTabs + 0.5 * topReq * (isFinite(topNorm) && topNorm > 0 ? topNorm : 0);
      })() : 0;
      
      // Store the EWPS on the user object for potential display
      user.ewps = Math.round(userEWPS * 10) / 10; // Round to 1 decimal place
      
      return userEWPS > topEWPS ? user : top;
    }, null);

    const totalAcceptanceRate = users.reduce((sum, user) => sum + (user.acceptanceRate || 0), 0);
    const avgAcceptanceRate = totalUsers > 0 ? Math.round(totalAcceptanceRate / totalUsers) : 0;

    const totalActiveDays = users.reduce((sum, user) => sum + (user.activeDays || 0), 0);
    const avgActiveDays = totalUsers > 0 ? Math.round(totalActiveDays / totalUsers) : 0;

    return {
      totalUsers,
      activeUsers,
      avgProductivity,
      topPerformer,
      avgAcceptanceRate,
      avgActiveDays
    };
  };

  const getSortedUsers = () => {
    if (!usersData?.users?.length) return [];

    return [...usersData.users].map(user => {
      // Calculate total requests (including bugbot)
      const totalRequests = sumUserRequestTotals(user);
      
      // Calculate EWPS using the formula:
      // EWPS = totalRequests + (0.08 × totalTabsAccepted) + [ ln(1 + totalLinesAdded) / ln(1 + 1000) × 0.5 × totalRequests ]
      const normLines = Math.log(1 + metricNumber(user.totalLinesAdded)) / Math.log(1 + 1000);
      const ewps = (totalRequests || 0)
        + 0.08 * metricNumber(user.totalTabsAccepted)
        + 0.5 * totalRequests * (isFinite(normLines) && normLines > 0 ? normLines : 0);
      
      // Calculate Requests/Active Day
      const requestsPerDay = user.activeDays > 0 
        ? Math.round((totalRequests / user.activeDays) * 10) / 10 
        : 0;
        
      // Shorten model label to keep table compact
      const shortModel = user.mostUsedModel ? String(user.mostUsedModel).replace(/^anthropic\./, '').replace(/^openai\./, '') : '';

      return {
        ...user,
        // Store total requests for display
        totalRequests: totalRequests,
        mostUsedModelShort: shortModel,
        modelSortKey: (shortModel || '').toLowerCase(),
        // Store EWPS for sorting and display (ensure it's different from totalRequests)
        ewps: Math.round(ewps * 10) / 10, // Round to 1 decimal place
        // Store Requests/Active Day
        requestsPerDay: requestsPerDay
      };
    }).sort((a, b) => {
      let aValue, bValue;
      
      if (sortBy === 'TabsAccepted') {
        aValue = a.totalTabsAccepted || 0;
        bValue = b.totalTabsAccepted || 0;
      } else if (sortBy === 'totalLinesAdded') {
        aValue = a.totalLinesAdded || 0;
        bValue = b.totalLinesAdded || 0;
      } else {
        aValue = a[sortBy];
        bValue = b[sortBy];
      }

      // String-aware and number-aware sorting
      const aIsString = typeof aValue === 'string';
      const bIsString = typeof bValue === 'string';
      if (aIsString || bIsString) {
        const aStr = (aValue || '').toString();
        const bStr = (bValue || '').toString();
        const cmp = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
        return sortOrder === 'desc' ? -cmp : cmp;
      }
      const aNum = Number(aValue) || 0;
      const bNum = Number(bValue) || 0;
      return sortOrder === 'desc' ? bNum - aNum : aNum - bNum;
    });
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      // Default to descending order when changing columns
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getProductivityDistribution = () => {
    if (!usersData?.users?.length) return [];

    // Calculate EWPS for each user using the same formula as for top performer
    // EWPS = totalRequests + (0.08 × totalTabsAccepted) + [ ln(1 + totalLinesAdded) / ln(1 + 1000) × 0.5 × totalRequests ]
    const users = usersData.users.map(user => {
      // Calculate total requests (including bugbot)
      const totalRequests = sumUserRequestTotals(user);
      
      // Calculate EWPS using the new formula
      const nl = Math.log(1 + metricNumber(user.totalLinesAdded)) / Math.log(1 + 1000);
      const ewps = totalRequests
        + 0.08 * metricNumber(user.totalTabsAccepted)
        + 0.5 * totalRequests * (isFinite(nl) && nl > 0 ? nl : 0);
      
      return {
        ...user,
        totalRequests: totalRequests,
        ewps: Math.round(ewps * 10) / 10 // Round to 1 decimal place
      };
    });
    
    if (users.length === 0) return [];
    
    // Sort users by EWPS to calculate percentiles
    const sortedUsers = users.sort((a, b) => a.ewps - b.ewps);
    const totalUsers = sortedUsers.length;
    
    // Calculate meaningful percentile-based ranges
    const getPercentileValue = (percentile) => {
      const index = Math.floor((percentile / 100) * (totalUsers - 1));
      return sortedUsers[index]?.ewps || 0;
    };
    
    const p20 = getPercentileValue(20);
    const p40 = getPercentileValue(40);
    const p60 = getPercentileValue(60);
    const p80 = getPercentileValue(80);
    
    // Create adaptive ranges based on actual data distribution
    const ranges = [
      {
        name: `Inactive (0)`,
        min: 0,
        max: 0,
        count: 0,
        description: 'No activity',
        color: '#9ca3af' // Gray color for inactive users
      },
      { 
        name: `Low (${p20 > 0 ? '1' : '0'}-${p20.toLocaleString()})`, 
        min: p20 > 0 ? 1 : 0.1, // If p20 is 0, use 0.1 as min to differentiate from inactive
        max: p20, 
        count: 0,
        description: 'Bottom 20%',
        color: '#ef4444' 
      },
      { 
        name: `Below Avg (${(p20 + 1).toLocaleString()}-${p40.toLocaleString()})`, 
        min: p20 + 1, 
        max: p40, 
        count: 0,
        description: '20th-40th percentile',
        color: '#f59e0b' 
      },
      { 
        name: `Average (${(p40 + 1).toLocaleString()}-${p60.toLocaleString()})`, 
        min: p40 + 1, 
        max: p60, 
        count: 0,
        description: '40th-60th percentile',
        color: '#10b981' 
      },
      { 
        name: `Above Avg (${(p60 + 1).toLocaleString()}-${p80.toLocaleString()})`, 
        min: p60 + 1, 
        max: p80, 
        count: 0,
        description: '60th-80th percentile',
        color: '#3b82f6' 
      },
      { 
        name: `High (${(p80 + 1).toLocaleString()}+)`, 
        min: p80 + 1, 
        max: Infinity, 
        count: 0,
        description: 'Top 20%',
        color: '#8b5cf6' 
      }
    ];

    // Count users in each range and track their names
    users.forEach(user => {
      const ewpsScore = user.ewps || 0;
      const range = ranges.find(r => ewpsScore >= r.min && ewpsScore <= r.max);
      if (range) {
        range.count++;
        // Add user to the range's users list
        if (!range.users) range.users = [];
        range.users.push({
          name: user.Email?.split('@')[0] || user.MemberName || 'Unknown',
          email: user.Email,
          requests: user.totalRequests || 0,
          ewps: ewpsScore
        });
      }
    });
    
    // Sort users in each range by EWPS (descending)
    ranges.forEach(range => {
      if (range.users) {
        range.users.sort((a, b) => b.ewps - a.ewps);
      }
    });

    return ranges;
  };

  // Feature adoption data is no longer needed as we've replaced that section

  const summary = calculateExecutiveSummary();
  const sortedUsers = getSortedUsers();
  const productivityDistribution = getProductivityDistribution();
  
  // Update lowEwpsUsers whenever usersData changes
  useEffect(() => {
    if (usersData?.users?.length) {
      // Get users with EWPS ≤ 3
      const lowUsers = usersData.users
        .filter(user => {
          const totalRequests = sumUserRequestTotals(user);
          
          const ewps = 1.0 * totalRequests + 
                       0.2 * metricNumber(user.totalLinesAdded) + 
                       0.5 * metricNumber(user.totalChatAccepts) + 
                       0.75 * metricNumber(user.totalTabsAccepted);
          
          return Math.round(ewps * 10) / 10 <= 3;
        })
        .map(user => {
          const totalRequests = sumUserRequestTotals(user);
          
          // Calculate EWPS using the updated formula
          // EWPS = totalRequests + (0.08 × totalTabsAccepted) + [ ln(1 + totalLinesAdded) / ln(1 + 1000) × 0.5 × totalRequests ]
          const norm = Math.log(1 + metricNumber(user.totalLinesAdded)) / Math.log(1 + 1000);
          const ewps = totalRequests
            + 0.08 * metricNumber(user.totalTabsAccepted)
            + 0.5 * totalRequests * (isFinite(norm) && norm > 0 ? norm : 0);
          
          return {
            name: user.Email?.split('@')[0] || user.MemberName || 'Unknown',
            email: user.Email,
            requests: totalRequests,
            ewps: Math.round(ewps * 10) / 10
          };
        })
        .sort((a, b) => a.ewps - b.ewps);
      
      setLowEwpsUsers(lowUsers);
    }
  }, [usersData]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
                          <h1 className="text-2xl font-bold text-gray-900">Developer Performance Analytics</h1>
          <p className="text-gray-600 mt-1">Individual developer insights and productivity metrics</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Include IT Checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="includeIT"
              checked={includeIT}
              onChange={(e) => setIncludeIT(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="includeIT" className="text-sm font-medium text-gray-700">
              Include IT
            </label>
          </div>
          <button
            onClick={loadUsersData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Date Range Filter */}
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Date Range:</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <DatePicker
                selected={dateRange.startDate}
                onChange={(date) => handleDateRangeChange('startDate', date)}
                selectsStart
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                maxDate={dateRange.endDate || endOfDay(new Date())}
                className="px-3 py-1 border border-gray-300 rounded text-sm"
                dateFormat="MMM d, yyyy"
              />
              <span className="text-gray-500">to</span>
              <DatePicker
                selected={dateRange.endDate}
                onChange={(date) => handleDateRangeChange('endDate', date)}
                selectsEnd
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                minDate={dateRange.startDate}
                maxDate={getMaxEndDate(dateRange.startDate)}
                className="px-3 py-1 border border-gray-300 rounded text-sm"
                dateFormat="MMM d, yyyy"
              />
            </div>
            <p className="text-xs text-gray-500 w-full sm:w-auto">
              Max {MAX_ANALYTICS_RANGE_DAYS} days. Earlier start auto-adjusts end; you can shorten it.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setDateRange(getQuickDateRange(7))}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
              >
                7d
              </button>
              <button
                onClick={() => setDateRange(getQuickDateRange(14))}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
              >
                14d
              </button>
              <button
                onClick={() => setDateRange(getQuickDateRange(30))}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
              >
                30d
              </button>
            </div>
          </div>

          {/* Team Filter */}
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Team:</span>
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded text-sm"
            >
              <option value="all">All Teams</option>
              {teamsData.map(team => (
                <option key={team.teamId} value={team.teamId}>
                  {team.teamName} ({team.totalUsers} users)
                </option>
              ))}
            </select>
          </div>

        </div>
      </div>

      {/* Executive Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center gap-3">
            <UsersIcon className="text-blue-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">Total Developers</p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center gap-3">
            <Activity className="text-green-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">Active Developers</p>
              <p className="text-2xl font-bold text-gray-900">
                {summary.activeUsers}<span className="text-sm text-gray-500">/{summary.totalUsers}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center gap-3">
            <Code className="text-purple-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">Avg Requests/User</p>
              <p className="text-2xl font-bold text-gray-900">{summary.avgProductivity}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center gap-3">
            <Target className="text-orange-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">Avg Acceptance</p>
              <p className="text-2xl font-bold text-gray-900">{summary.avgAcceptanceRate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Performer Highlight */}
      {summary.topPerformer && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <Star className="text-purple-600" size={24} />
            <div>
              <h3 className="text-lg font-semibold text-purple-900">Top Performer</h3>
              <p className="text-xs text-purple-600">Based on Engagement Weighted Performance Score (EWPS)</p>
            </div>
            <div className="ml-auto relative group">
              <Info size={16} className="text-purple-500 cursor-help" />
              <div className="absolute right-0 top-full mt-2 w-72 bg-white p-3 rounded-md shadow-lg border border-purple-100 hidden group-hover:block z-10">
                <p className="text-xs text-gray-700 font-medium mb-1">EWPS Formula:</p>
                <p className="text-xs text-gray-600">EWPS = totalRequests + (0.08 × totalTabsAccepted) + [ ln(1 + totalLinesAdded) / ln(1 + 1000) × 0.5 × totalRequests ]</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <p className="text-sm text-purple-600 font-medium">User</p>
              <p className="text-xl font-bold text-purple-900">{summary.topPerformer.Email?.split('@')[0]}</p>
            </div>
            <div>
              <p className="text-sm text-purple-600 font-medium">Team</p>
              <p className="text-xl font-bold text-purple-900">{summary.topPerformer.teamName || 'N/A'}</p>
            </div>
            <div>
              <div className="flex items-center gap-1">
                <p className="text-sm text-purple-600 font-medium">EWPS Score</p>
                <div className="relative group">
                  <Info size={14} className="text-purple-400 cursor-help" />
                  <div className="absolute left-0 top-full mt-2 w-72 bg-white p-3 rounded-md shadow-lg border border-purple-100 hidden group-hover:block z-10">
                    <p className="text-xs text-gray-700 font-medium mb-1">EWPS Formula:</p>
                    <p className="text-xs text-gray-600">EWPS = totalRequests + (0.08 × totalTabsAccepted) + [ ln(1 + totalLinesAdded) / ln(1 + 1000) × 0.5 × totalRequests ]</p>
                  </div>
                </div>
              </div>
              <p className="text-xl font-bold text-purple-900">{summary.topPerformer.ewps?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-purple-600 font-medium">Total Requests</p>
              <p className="text-xl font-bold text-purple-900">{formatIntegerMetric(sumUserRequestTotals(summary.topPerformer))}</p>
            </div>
            <div>
              <p className="text-sm text-purple-600 font-medium">Acceptance Rate</p>
              <p className="text-xl font-bold text-purple-900">{summary.topPerformer.acceptanceRate}%</p>
            </div>
            <div>
              <p className="text-sm text-purple-600 font-medium">Active Days</p>
              <p className="text-xl font-bold text-purple-900">{summary.topPerformer.activeDays}</p>
            </div>
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Productivity Distribution */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">User Productivity Distribution</h3>
            <p className="text-sm text-gray-600">Performance tiers based on EWPS score (percentile-based ranges)</p>
          </div>
          {productivityDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={productivityDistribution} margin={{ bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={80}
                  interval={0}
                  fontSize={12}
                />
                <YAxis label={{ value: 'Number of Users', angle: -90, position: 'insideLeft', dx: -15, dy: -40 }} />
                <Tooltip 
                  formatter={(value, name, props) => {
                    return [
                      `${value} users`,
                      props.payload.description
                    ];
                  }}
                  labelFormatter={(label) => `Performance Tier: ${label}`}
                />
                <Bar 
                  dataKey="count" 
                  radius={[4, 4, 0, 0]}
                  onClick={(data) => setSelectedRange(data)}
                  cursor="pointer"
                >
                  {productivityDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">No productivity data available</div>
          )}
        </div>

        {/* Selected Developers */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {selectedRange ? `Developers in ${selectedRange.name} Range` : 'Developers in Low (≤3) Range'}
          </h3>
          {(selectedRange && selectedRange.users && selectedRange.users.length > 0) || (!selectedRange && lowEwpsUsers.length > 0) ? (
            <div className="overflow-y-auto max-h-[300px]">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Developer</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">EWPS</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requests</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(selectedRange ? selectedRange.users : lowEwpsUsers).map((user, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-2 text-sm text-gray-900">{user.name}</td>
                      <td className="px-4 py-2 text-sm font-medium text-purple-700">{(user.ewps || 0).toLocaleString()}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{formatIntegerMetric(user.requests)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              {selectedRange ? 'No developers in this range' : 'No developers with low EWPS found'}
            </div>
          )}
        </div>
      </div>

      {/* User Performance Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Developer Performance Leaderboard</h3>
              <p className="text-xs text-gray-500 mt-1">Showing top 20 performers</p>
            </div>
            <div className="text-sm text-gray-600">
              Click on column headers to sort
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto pb-2">
          <table className="min-w-[1400px]">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                  User
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 relative group"
                  onClick={() => handleSort('ewps')}
                >
                  <div className="flex items-center">
                    EWPS {sortBy === 'ewps' && (sortOrder === 'desc' ? '↓' : '↑')}
                    <Info size={14} className="ml-1 text-gray-400 cursor-help" />
                    <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 w-72 bg-white p-3 rounded-md shadow-lg border border-gray-100 hidden group-hover:block z-10">
                      <p className="text-xs text-gray-700 font-medium mb-1">EWPS Formula:</p>
                      <p className="text-xs text-gray-600">EWPS = totalRequests + (0.08 × totalTabsAccepted) + [ ln(1 + totalLinesAdded) / ln(1 + 1000) × 0.5 × totalRequests ]</p>
                    </div>
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalRequests')}
                >
                  <div className="flex items-center gap-1">
                    Requests {sortBy === 'totalRequests' && (sortOrder === 'desc' ? '↓' : '↑')}
                    <div className="relative group">
                      <Info size={14} className="text-gray-400 cursor-help" />
                      <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 w-72 bg-white p-3 rounded-md shadow-lg border border-gray-100 hidden group-hover:block z-10">
                        <p className="text-xs text-gray-600">Total Requests Submitted</p>
                      </div>
                    </div>
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('acceptanceRate')}
                >
                  Acceptance Rate {sortBy === 'acceptanceRate' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('activeDays')}
                >
                  Active Days {sortBy === 'activeDays' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('requestsPerDay')}
                >
                  Requests/Day {sortBy === 'requestsPerDay' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('mostUsedModelShort')}
                >
                  <div className="flex items-center gap-1">
                    Model {sortBy === 'mostUsedModelShort' && (sortOrder === 'desc' ? '↓' : '↑')}
                    <div className="relative group">
                      <Info size={14} className="text-gray-400 cursor-help" />
                      <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 w-64 bg-white p-2 rounded-md shadow-lg border hidden group-hover:block z-10">
                        <p className="text-xs text-gray-600">Most Commonly used Model</p>
                      </div>
                    </div>
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalTokensK')}
                >
                  <div className="flex items-center gap-1">
                    Tokens (K) {sortBy === 'totalTokensK' && (sortOrder === 'desc' ? '↓' : '↑')}
                    <div className="relative group">
                      <Info size={14} className="text-gray-400 cursor-help" />
                      <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 w-72 bg-white p-2 rounded-md shadow-lg border hidden group-hover:block z-10">
                        <p className="text-xs text-gray-600">Total Tokens Used</p>
                        <p className="text-xs text-gray-500 mt-1">Tokens are only consumed on usage-based events.</p>
                      </div>
                    </div>
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('TabsAccepted')}
                >
                  <div className="flex items-center gap-1">
                    Tabs {sortBy === 'TabsAccepted' && (sortOrder === 'desc' ? '↓' : '↑')}
                    <div className="relative group">
                      <Info size={14} className="text-gray-400 cursor-help" />
                      <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 w-56 bg-white p-2 rounded-md shadow-lg border hidden group-hover:block z-10">
                        <p className="text-xs text-gray-600">Total Tabs Accepted</p>
                      </div>
                    </div>
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalLinesAdded')}
                >
                  <div className="flex items-center gap-1">
                    Lines Added {sortBy === 'totalLinesAdded' && (sortOrder === 'desc' ? '↓' : '↑')}
                    <div className="relative group">
                      <Info size={14} className="text-gray-400 cursor-help" />
                      <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 w-64 bg-white p-2 rounded-md shadow-lg border hidden group-hover:block z-10">
                        <p className="text-xs text-gray-600">Total Lines Added</p>
                      </div>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedUsers.length > 0 ? sortedUsers.slice(0, 20).map((user, index) => (
                <tr key={user.Id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {index === 0 && <Star className="text-yellow-500 mr-2" size={16} />}
                      <span className="text-sm font-medium text-gray-900">#{index + 1}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                      </div>
                      <div className="ml-3 max-w-[160px] truncate">
                        <Link to={`/users/${user.userId || user.Id}`} className="text-sm font-medium text-blue-600 hover:underline truncate">
                          {user.Email?.split('@')[0] || 'Unknown'}
                        </Link>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {user.teamName || 'Unassigned'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-purple-700">
                      {(user.ewps || 0).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">
                      {formatIntegerMetric(user.totalRequests)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm text-gray-900">{user.acceptanceRate || 0}%</div>
                      {(user.acceptanceRate || 0) >= 80 && (
                        <CheckCircle className="ml-2 text-green-500" size={16} />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.activeDays || 0} days</div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 font-medium">
                      {(user.requestsPerDay || 0).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 truncate max-w-[140px]" title={user.mostUsedModel || ''}>
                      {user.mostUsedModelShort || ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(user.totalTokensK || 0)}</div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatIntegerMetric(user.totalTabsAccepted)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatIntegerMetric(user.totalLinesAdded)}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                    No user data available for the selected filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Users; 